import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, PolicyStatusPill, FingerprintChip } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, XCircle, Archive, Edit, PenLine, ShieldCheck,
  Users, Calendar, Fingerprint as FingerprintIcon, RotateCcw,
} from "lucide-react";

export default function PolicyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [myAcks, setMyAcks] = useState([]);
  const [allAcks, setAllAcks] = useState([]);
  const [signName, setSignName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/policies/${id}`);
      setP(data);
    } catch { toast.error("Failed to load policy"); nav("/policies"); }
  };
  const loadAcks = async () => {
    try {
      const { data } = await api.get(`/my/policy-acknowledgments`);
      setMyAcks(data);
    } catch { /* ignore */ }
  };
  const loadAllAcks = async () => {
    if (!["hr", "company_admin", "super_admin"].includes(user.role)) return;
    try {
      const { data } = await api.get(`/policies/${id}/acknowledgments`);
      setAllAcks(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadAcks(); }, [id]); // eslint-disable-line
  useEffect(() => { if (p) loadAllAcks(); }, [p?.id]); // eslint-disable-line

  if (!p) return <div className="p-16 text-center text-slate-500 font-mono text-sm">Loading policy…</div>;

  const canApprove = ["company_admin", "super_admin"].includes(user.role);
  const canEdit = ["hr", "company_admin", "super_admin"].includes(user.role) && ["draft", "rejected"].includes(p.status);
  const canArchive = ["company_admin", "super_admin"].includes(user.role) && p.status === "approved";
  const canManage = ["hr", "company_admin", "super_admin"].includes(user.role);
  const alreadyAcknowledged = myAcks.some((a) => a.policy_id === p.id && a.policy_fingerprint === p.fingerprint);
  const isReaderView = ["employee", "manager"].includes(user.role);
  const isApproved = p.status === "approved";
  const needsSignature = isReaderView && isApproved && !alreadyAcknowledged;

  const approve = async () => {
    try { const { data } = await api.post(`/policies/${id}/approve`); setP(data); toast.success("Policy approved"); }
    catch { toast.error("Failed"); }
  };
  const reject = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    try { const { data } = await api.post(`/policies/${id}/reject`, { reason }); setP(data); setRejecting(false); toast.success("Rejected"); }
    catch { toast.error("Failed"); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this approved policy?")) return;
    try { const { data } = await api.post(`/policies/${id}/archive`); setP(data); toast.success("Archived"); }
    catch { toast.error("Failed"); }
  };

  const doSign = async () => {
    if (!agree) return toast.error("You must check the agreement box");
    if (!signName.trim()) return toast.error("Please type your full name to sign");
    setSigning(true);
    try {
      await api.post(`/policies/${id}/acknowledge`, { signed_name: signName, agree: true });
      toast.success("Policy acknowledged & signed");
      loadAcks();
      loadAllAcks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to sign"); }
    finally { setSigning(false); }
  };

  const backTo = ["hr", "company_admin", "super_admin"].includes(user.role) ? "/resources/policies" : "/policies";

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <Link to={backTo} className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL POLICIES
      </Link>

      <SectionHeader
        title={p.name}
        subtitle={p.summary}
        editorial
        right={
          <div className="flex flex-wrap gap-2 items-center">
            {isReaderView ? (
              alreadyAcknowledged ? (
                <span className="pill pill-approved">
                  <CheckCircle2 size={10} strokeWidth={3}/> Signed
                </span>
              ) : (
                <span className="pill pill-medium">
                  <PenLine size={10} strokeWidth={3}/> Needs your signature
                </span>
              )
            ) : (
              <>
                <PolicyStatusPill status={p.status}/>
                {alreadyAcknowledged && (
                  <span className="pill pill-approved">
                    <CheckCircle2 size={10} strokeWidth={3}/> Signed
                  </span>
                )}
              </>
            )}
            <span className="text-[10px] font-mono text-slate-500">v{p.version}</span>
          </div>
        }
      />

      {/* Recall banner , if this policy has been recalled, warn everyone */}
      {p.recall_reason && (
        <div className="border border-amber-300 bg-amber-50 p-4 rounded mb-4 flex items-start gap-3" data-testid="policy-detail-recall-banner">
          <RotateCcw size={16} className="text-amber-800 mt-0.5 shrink-0" strokeWidth={2}/>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-800 mb-1">Recalled &middot; re-signature required</div>
            <div className="text-sm text-amber-900 leading-relaxed">
              {p.recall_reason}
            </div>
            {p.recalled_at && (
              <div className="text-[11px] font-mono text-amber-700 mt-1">
                by {p.recalled_by_name || "Company Admin"} &middot; {new Date(p.recalled_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Meta */}
      <div className="data-cell p-4 mb-4 grid md:grid-cols-4 gap-4 text-xs">
        <Meta icon={ShieldCheck} label="Category" value={p.category}/>
        <Meta icon={Calendar} label="Effective" value={p.effective_from ? new Date(p.effective_from).toLocaleDateString() : "Not published"} mono/>
        <Meta icon={Users} label="Author" value={p.created_by_name || ","}/>
        <Meta icon={CheckCircle2} label="Approved by" value={p.approved_by_name || ","}/>
        <div className="md:col-span-4">
          <div className="eyebrow mb-1">Policy fingerprint (SHA-256)</div>
          <FingerprintChip fingerprint={p.fingerprint}/>
        </div>
      </div>

      {p.status === "rejected" && p.rejection_reason && (
        <div className="border-l-4 border-red-600 bg-red-50 p-4 mb-4">
          <div className="text-xs font-mono text-red-900 uppercase tracking-wider mb-1">Rejected</div>
          <div className="text-sm text-red-900">{p.rejection_reason}</div>
        </div>
      )}

      {/* Manage actions */}
      {canManage && (
        <div className="flex flex-wrap gap-2 mb-6">
          {canEdit && <Link to={`/resources/policies?edit=${p.id}`} className="btn-ghost"><Edit size={14} strokeWidth={2}/> Edit</Link>}
          {canApprove && p.status === "pending_approval" && (
            <>
              <button className="btn-primary" onClick={approve} data-testid={TID.policyApprove}><CheckCircle2 size={14}/> Approve</button>
              <button className="btn-danger" onClick={() => setRejecting(true)} data-testid={TID.policyReject}><XCircle size={14}/> Reject</button>
            </>
          )}
          {canArchive && <button className="btn-ghost text-red-700" onClick={archive}><Archive size={14}/> Archive</button>}
        </div>
      )}

      {/* Enterprise reading pane */}
      <div className="grid lg:grid-cols-3 gap-6">
        <motion.article
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="lg:col-span-2 policy-parchment"
        >
          <div className="text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-2 flex items-center gap-3">
            <span>Confidential</span>
            <span>·</span>
            <span>PropHR Policy Library</span>
            <span>·</span>
            <span>v{p.version}</span>
          </div>
          <h1 className="font-editorial text-3xl md:text-4xl text-slate-900 leading-tight mb-2">{p.name}</h1>
          {p.summary && <p className="text-slate-600 text-base italic mb-6">{p.summary}</p>}
          <div className="policy-content text-slate-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: p.content }}/>
          <div className="mt-10 pt-5 border-t border-slate-200 text-[11px] font-mono text-slate-500 flex flex-wrap items-center justify-between gap-2">
            <span>Fingerprint locks the exact text you are agreeing to.</span>
            <FingerprintChip fingerprint={p.fingerprint}/>
          </div>
        </motion.article>

        {/* Sign & agree */}
        <div className="space-y-4">
          {isApproved && !alreadyAcknowledged && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-slate-900 bg-white p-5 shadow-sm sticky top-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <PenLine size={16} strokeWidth={2} className="text-slate-900"/>
                <div className="eyebrow">Sign & agree</div>
              </div>
              <p className="text-[13px] text-slate-700 leading-relaxed mb-4">
                To accept this policy on the record, type your full legal name below. Your signature will be sealed with a SHA-256 fingerprint that includes the policy version and timestamp.
              </p>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1 block">Full legal name (must match "{user.name}")</label>
              <input
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
                className="input font-editorial text-lg mb-3"
                placeholder={user.name}
                data-testid={TID.policyAcknowledgeName}
                autoComplete="off"
              />
              <label className="flex items-start gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  data-testid={TID.policyAcknowledgeAgree}
                />
                <span className="text-[12px] leading-relaxed text-slate-700">
                  I have read, understood, and agree to comply with this policy as an employee of my organization.
                </span>
              </label>
              <button
                className="btn-primary w-full"
                onClick={doSign}
                disabled={signing}
                data-testid={TID.policyAcknowledgeSubmit}
              >
                <PenLine size={14} strokeWidth={2}/> {signing ? "Signing…" : "Sign policy"}
              </button>
              <div className="mt-3 text-[10px] font-mono text-slate-400 leading-relaxed">
                Fingerprinted with SHA-256 · timestamped · retained per your organization's retention policy
              </div>
            </motion.div>
          )}

          {alreadyAcknowledged && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-green-600 bg-green-50 p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={18} strokeWidth={2} className="text-green-700"/>
                <div className="font-semibold text-green-900">You have signed this policy</div>
              </div>
              <p className="text-[12px] text-green-900 leading-relaxed">
                Your acknowledgment is on file. If the policy is revised, you'll be asked to re-sign the new version.
              </p>
            </motion.div>
          )}

          {canManage && (
            <div className="data-cell p-5">
              <div className="flex items-center gap-2 mb-3">
                <FingerprintIcon size={14} strokeWidth={2}/>
                <div className="eyebrow">Acknowledgments</div>
              </div>
              <div className="text-3xl font-display text-slate-900 mb-1">{allAcks.length}</div>
              <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mb-3">signatures collected</div>
              {allAcks.length > 0 && (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {allAcks.slice(0, 15).map((a) => (
                    <div key={a.id} className="border border-slate-200 p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-900 truncate">{a.user_name}</span>
                        <span className="text-[10px] font-mono text-slate-500">v{a.policy_version}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">{a.user_email}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{new Date(a.created_at).toLocaleString()}</div>
                      <div className="mt-1"><FingerprintChip fingerprint={a.acknowledgment_fingerprint}/></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!isApproved && (
            <div className="border border-amber-300 bg-amber-50 p-4 text-[12px] text-amber-900">
              Signing is only available on <span className="font-semibold">approved</span> policies.
            </div>
          )}
        </div>
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-300 max-w-md w-full p-6 rounded">
            <h3 className="font-display text-xl font-semibold mb-3">Reject Policy</h3>
            <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain the reason for rejection…" className="input mb-4"/>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setRejecting(false)}>Cancel</button>
              <button className="btn-danger" onClick={reject}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="eyebrow mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={2}/>}{label}
      </div>
      <div className={"text-sm text-slate-900 " + (mono ? "font-mono" : "")}>{value}</div>
    </div>
  );
}
